import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import { addDefaultErrorMessageAction } from 'containers/FlashMessage/actions';
import { apiGet } from 'utils/axios';

export default function useFetchOpenings() {
  const dispatch = useDispatch();
  const [openings, setOpenings] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  // Guards against a slow, stale fetch (e.g. the page-mount request) resolving
  // after a newer one (e.g. a search) and clobbering its results.
  const seq = useRef(0);

  const fetchOpenings = useCallback(
    async params => {
      const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/openings`;
      seq.current += 1;
      const id = seq.current;

      setIsFetching(true);
      try {
        const { data } = await apiGet(url, params);
        if (id !== seq.current) return;

        const camelizedPagination = {
          currentPage: data.pagination.current_page,
          nextPage: data.pagination.next_page,
          totalCount: data.pagination.total_count,
          totalPages: data.pagination.total_pages,
        };
        setOpenings(data.openings);
        setPagination(camelizedPagination);
        setError(null);
      } catch (e) {
        if (id !== seq.current) return;

        setError(e);
        dispatch(addDefaultErrorMessageAction());
      } finally {
        if (id === seq.current) setIsFetching(false);
      }
    },
    [dispatch],
  );

  const updateOpening = useCallback((externalId, update) => {
    setOpenings(current =>
      current?.map(opening =>
        opening.funnel_external_id === externalId
          ? { ...opening, ...update }
          : opening,
      ),
    );
  }, []);

  return {
    fetchOpenings,
    updateOpening,
    isFetching,
    error,
    openings,
    pagination,
  };
}
